import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../types/alert.ts';
import { formatCurrency, formatDate } from '../../utils/formatters.ts';
import { calculateDueDays, formatDueDays } from '../../utils/dateUtils.ts';

interface AlertsProps {
  paymentAlerts: Alert[];
  leaseAlerts: Array<{
    tenant_id: string;
    tenant_name: string;
    property_name: string;
    end_date: string;
    days_remaining: number;
  }>;
}

const Alerts: React.FC<AlertsProps> = ({ paymentAlerts, leaseAlerts }) => {
  const navigate = useNavigate();

  const handleRecordPayment = (alert: Alert) => {
    navigate('/payments', { 
      state: { 
        openPaymentForm: true,
        selectedTenantId: alert.tenant_id,
        selectedBillId: alert.bill_id,
        amount: (alert.amount || 0) - (alert.paid_amount || 0)
      }
    });
  };

  const getAlertMessage = (alert: any) => {
    const pendingAmount = alert.amount - (alert.paid_amount || 0);
    const dueDays = calculateDueDays(alert.due_date);
    const dueText = formatDueDays(dueDays);
    
    let message = `Payment of ${formatCurrency(pendingAmount)} ${dueText}`;
    
    if (alert.paid_amount > 0) {
      message += ` (Original: ${formatCurrency(alert.amount)}, Paid: ${formatCurrency(alert.paid_amount)})`;
    }
    
    return message;
  };

  const getPaymentStatusDetails = (alert: any) => {
    const pendingAmount = alert.amount - (alert.paid_amount || 0);
    const hasPartialPayment = alert.paid_amount > 0;

    if (hasPartialPayment) {
      return (
        <div className="payment-breakdown">
          <span className="total-amount">Original: {formatCurrency(alert.amount)}</span>
          <span className="paid-amount">Paid: {formatCurrency(alert.paid_amount)}</span>
          <span className="pending-amount">Pending: {formatCurrency(pendingAmount)}</span>
        </div>
      );
    }
    
    return <span className="alert-amount">{formatCurrency(alert.amount)}</span>;
  };

  if (paymentAlerts.length === 0 && leaseAlerts.length === 0) {
    return null;
  }

  return (
    <div className="alerts-section">
      <h2>Alerts</h2>
      <div className="alerts-list">
        {/* Payment Alerts */}
        {paymentAlerts.map((alert) => (
          <div 
            key={alert.id} 
            className={`alert-card ${calculateDueDays(alert.due_date) < 0 ? 'alert-danger' : 'alert-warning'}`}
          >
            <div className="alert-content">
              <div className="alert-header">
                <span className="alert-type">
                  {calculateDueDays(alert.due_date) < 0 ? '🚨 Overdue' : '⚠️ Due Soon'}
                </span>
                {getPaymentStatusDetails(alert)}
              </div>
              <div className="alert-body">
                <p className="tenant-name">{alert.tenant_name}</p>
                <p className="alert-property">{alert.property_name}</p>
                <p className="alert-message">{getAlertMessage(alert)}</p>
                <p className="alert-date">Due date: {formatDate(alert.due_date)}</p>
              </div>
            </div>
            <div className="alert-footer">
              <button 
                className="btn-primary"
                onClick={() => handleRecordPayment(alert)}
              >
                Record Payment
              </button>
            </div>
          </div>
        ))}

        {/* Lease Ending Alerts */}
        {leaseAlerts.map((lease) => (
          <div 
            key={lease.tenant_id} 
            className={`alert-card ${lease.days_remaining <= 30 ? 'alert-warning' : 'alert-info'}`}
          >
            <div className="alert-header">
              <span className="alert-type">📅 Lease Ending</span>
              <span className={`days-remaining ${lease.days_remaining <= 30 ? 'urgent' : ''}`}>
                {lease.days_remaining} days remaining
              </span>
            </div>
            <div className="alert-body">
              <p>{lease.tenant_name}</p>
              <p className="alert-property">{lease.property_name}</p>
              <p className="alert-date">End date: {formatDate(lease.end_date)}</p>
            </div>
            <div className="alert-footer">
              <button 
                className="btn-secondary"
                onClick={() => navigate(`/tenants/${lease.tenant_id}`)}
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Alerts;
